import { motion } from "framer-motion";
import { styled } from "@mui/material/styles";
import { Box, Typography, useTheme, Tooltip, IconButton } from "@mui/material";
import PerfectScrollbar from "react-perfect-scrollbar";
import "react-perfect-scrollbar/dist/css/styles.css";
import ArrowBack from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";

const StyledBackButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(2),
  right: theme.spacing(2),
  backgroundColor: "#f2efe9",
  color: "#455354",
  transition: "background 0.24s ease-in-out, color 0.24s ease-in-out",
  "&:hover": {
    background: "linear-gradient(to bottom, #758d8e, #4b5b5d)",
    color: "#f2efe9",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.75),
  },
}));

const Container = styled(Box)(({ theme }) => ({
  minHeight: "95vh",
  background: "#fff",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(2),
  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(2),
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(3),
  },
}));

const Content = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: 1200,
  height: "auto",
  padding: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  background: "#fff",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
    maxWidth: "100%",
  },
}));

const WelcomeText = styled(Typography)(({ theme }) => ({
  fontFamily: "'Almarai', sans-serif",
  fontSize: "45px",
  fontWeight: 600,
  color: "#4b5b5d",
  textAlign: "center",
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    fontSize: "30px",
  },
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  fontFamily: "'Almarai', sans-serif",
  color: "#757575",
  fontSize: "16px",
  marginBottom: theme.spacing(3),
  textAlign: "center",
  [theme.breakpoints.down("sm")]: {
    fontSize: "12px",
    marginBottom: theme.spacing(2),
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "'Almarai', sans-serif",
  fontSize: "22px",
  fontWeight: 600,
  color: "#4b5b5d",
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(2),
  alignSelf: "flex-start",
  [theme.breakpoints.down("sm")]: {
    fontSize: "18px",
    textAlign: "center",
    alignSelf: "center",
  },
}));

const SubsectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "'Almarai', sans-serif",
  fontSize: "18px",
  fontWeight: 600,
  color: "#4b5b5d",
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(1),
  alignSelf: "flex-start",
  [theme.breakpoints.down("sm")]: {
    fontSize: "16px",
    textAlign: "center",
    alignSelf: "center",
  },
}));

const Text = styled(Typography)(({ theme }) => ({
  fontFamily: "'Almarai', sans-serif",
  fontSize: "16px",
  color: "#757575",
  marginBottom: theme.spacing(1.5),
  lineHeight: 1.8,
  alignSelf: "flex-start",
  [theme.breakpoints.down("sm")]: {
    fontSize: "14px",
    textAlign: "justify",
    alignSelf: "center",
  },
}));

const ListItemStyled = styled(Typography)(({ theme }) => ({
  fontFamily: "'Almarai', sans-serif",
  fontSize: "16px",
  color: "#757575",
  marginBottom: theme.spacing(1),
  paddingLeft: theme.spacing(3),
  position: "relative",
  lineHeight: 1.8,
  alignSelf: "flex-start",
  "&:before": {
    content: '"•"',
    position: "absolute",
    left: theme.spacing(1),
    color: "#4b5b5d",
    fontSize: "20px",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "14px",
    paddingLeft: theme.spacing(2.5),
    textAlign: "justify",
    alignSelf: "center",
  },
}));

const TermsAndConditions = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const handleBack = () => {
    navigate("/register");
  };

  return (
    <Container>
      <Tooltip title="Back to Register" placement="left">
        <StyledBackButton
          aria-label="back"
          onClick={handleBack}
          sx={{
            zIndex: 10,
            [theme.breakpoints.down("sm")]: {
              top: theme.spacing(1.5),
              right: theme.spacing(1.5),
            },
          }}
        >
          <ArrowBack />
        </StyledBackButton>
      </Tooltip>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Content>
          <WelcomeText>Terms & Privacy</WelcomeText>
          <Subtitle>
            Please review our Terms of Use, Legal Disclaimers, and Privacy
            Policy below.
          </Subtitle>
          <PerfectScrollbar
            options={{ suppressScrollX: true }}
            style={{
              maxHeight: "calc(100vh - 250px)",
              overflow: "hidden",
              width: "100%",
            }}
          >
            <SectionTitle>Terms of Use</SectionTitle>
            <Text>
              <strong>Acceptance of Terms:</strong> By accessing or using the L2
              Charging Station ROI Estimator (the "Estimator") provided by
              RightSite LLC ("RightSite"), you agree to be bound by these Terms
              of Use. If you disagree with any part of these terms, you may not
              use the Estimator.
            </Text>
            <Text>
              <strong>Use of Estimator:</strong> The Estimator is intended
              solely for informational and planning purposes related to the
              installation and operation of Level 2 EV charging stations. Users
              are granted a limited, non-exclusive, non-transferable license to
              use the Estimator only for their personal or internal business
              use.
            </Text>
            <Text>
              <strong>Intellectual Property:</strong> All content, software,
              designs, algorithms, trademarks, and other intellectual property
              rights associated with the Estimator are owned exclusively by
              RightSite LLC. Users may not copy, modify, distribute, sell, or
              lease any part of the Estimator or create derivative works without
              express written permission from RightSite.
            </Text>
            <Text>
              <strong>Prohibited Activities:</strong> Users agree not to:
            </Text>
            <ListItemStyled>
              Misuse or interfere with the Estimator’s operation.
            </ListItemStyled>
            <ListItemStyled>
              Use the Estimator for unlawful or unauthorized purposes.
            </ListItemStyled>
            <ListItemStyled>
              Attempt to reverse engineer or disrupt any functionality of the
              Estimator.
            </ListItemStyled>
            <ListItemStyled>
              Share their login credentials or access privileges with
              unauthorized persons.
            </ListItemStyled>
            <Text>
              <strong>Accuracy of Information:</strong> RightSite makes
              reasonable efforts to ensure the Estimator provides accurate and
              current information. However, all estimates, forecasts, and
              projections are inherently uncertain and should not be relied upon
              exclusively for investment or financial decisions.
            </Text>
            <Text>
              <strong>Disclaimer of Warranties:</strong> The Estimator is
              provided on an "as is" and "as available" basis without warranties
              of any kind, either express or implied, including but not limited
              to accuracy, reliability, merchantability, fitness for a
              particular purpose, or non-infringement.
            </Text>
            <Text>
              <strong>Limitation of Liability:</strong> RightSite will not be
              liable for any direct, indirect, incidental, consequential,
              special, punitive, or exemplary damages arising from the use or
              inability to use the Estimator, including, without limitation,
              damages for loss of profits, goodwill, or data.
            </Text>
            <Text>
              <strong>Changes to Terms:</strong> RightSite reserves the right to
              modify or revise these Terms of Use at any time. Users will be
              notified of significant changes through the Estimator or via
              email.
            </Text>
            <Text>
              <strong>Governing Law and Jurisdiction:</strong> These Terms shall
              be governed by and construed in accordance with the laws of the
              State of Kansas, United States, without regard to conflict of law
              principles. Users consent to the exclusive jurisdiction of courts
              located within Kansas for resolving disputes arising from or
              related to these Terms of Use.
            </Text>
            <Text>
              <strong>Contact Information:</strong> For any questions regarding
              these Terms of Use, please contact RightSite LLC at:
              info@rightsite.net or 6218 Ash Street, Mission, KS 66205, United
              States, (816) 645-4924.
            </Text>

            <SectionTitle>Legal Disclaimers</SectionTitle>
            <SubsectionTitle>1. General Information</SubsectionTitle>
            <Text>
              The results provided by RightSite's L2 Charging Station ROI
              Estimator ("Estimator") are intended solely for informational
              purposes. These results do not represent financial, legal,
              investment, or professional advice. Users should consult qualified
              professionals before making investment or financial decisions.
            </Text>
            <SubsectionTitle>2. Accuracy and Projections</SubsectionTitle>
            <Text>
              RightSite makes reasonable efforts to ensure the accuracy of the
              Estimator's calculations; however, estimates are based on
              assumptions, averages, and user-provided inputs. Actual results
              may significantly differ due to changes in energy costs, market
              conditions, usage patterns, and other unforeseen factors.
            </Text>
            <SubsectionTitle>
              3. Financial and Investment Decisions
            </SubsectionTitle>
            <Text>
              RightSite provides no guarantee or warranty regarding financial
              returns, profitability, or investment outcomes suggested by the
              Estimator. Users accept full responsibility for independently
              verifying results and decisions based on the Estimator.
            </Text>
            <SubsectionTitle>
              4. Incentives, Grants, and Subsidies
            </SubsectionTitle>
            <Text>
              Any grants, subsidies, or tax credits shown in the Estimator are
              provided for illustrative purposes only. Users must independently
              confirm their eligibility and availability of these incentives
              with relevant regulatory or governmental bodies. RightSite assumes
              no responsibility for the accuracy or applicability of incentive
              data.
            </Text>
            <SubsectionTitle>5. Limitation of Liability</SubsectionTitle>
            <Text>
              RightSite shall not be liable for any direct, indirect,
              incidental, consequential, special, exemplary, punitive, or other
              damages arising from the use or reliance on the Estimator,
              including but not limited to lost profits, loss of data, or
              business interruption, regardless of legal theory or notice of
              potential damages.
            </Text>
            <SubsectionTitle>6. Warranty Disclaimer</SubsectionTitle>
            <Text>
              The Estimator is provided "as-is" without any warranty, either
              express or implied, including warranties regarding accuracy,
              completeness, merchantability, fitness for a particular purpose,
              or non-infringement.
            </Text>
            <SubsectionTitle>7. Third-party Information</SubsectionTitle>
            <Text>
              The Estimator may use or integrate data from third-party sources.
              RightSite is not responsible for errors, inaccuracies, omissions,
              or misrepresentations resulting from third-party data.
            </Text>
            <SubsectionTitle>8. Updates and Changes</SubsectionTitle>
            <Text>
              RightSite reserves the right to modify, update, or discontinue the
              Estimator's features or content at any time without notice or
              liability to users.
            </Text>
            <SubsectionTitle>9. Intellectual Property</SubsectionTitle>
            <Text>
              All content, calculations, trademarks, and intellectual property
              within the Estimator are owned by RightSite LLC and protected by
              applicable intellectual property laws. Use of the Estimator does
              not confer any rights to use or license RightSite’s intellectual
              property without express written consent.
            </Text>
            <SubsectionTitle>10. Acceptance of Terms</SubsectionTitle>
            <Text>
              By accessing or using the Estimator, users agree to these
              disclaimers and all associated terms and conditions. It is the
              user’s responsibility to review these disclaimers periodically for
              changes.
            </Text>

            <SectionTitle>Privacy Policy</SectionTitle>
            <Text>
              <strong>Effective Date:</strong> January 1st, 2025
            </Text>
            <Text>
              <strong>Introduction:</strong> RightSite LLC ("RightSite," "we,"
              "us," or "our") is committed to protecting your privacy. This
              Privacy Policy explains how your personal information is
              collected, used, and disclosed when you use our L2 Charging
              Station ROI Estimator ("Estimator").
            </Text>
            <Text>
              <strong>Information We Collect:</strong>
            </Text>
            <ListItemStyled>
              Personal Information: Names, email addresses, phone numbers,
              company names, and addresses provided voluntarily by users.
            </ListItemStyled>
            <ListItemStyled>
              Technical Information: IP addresses, browser types, access times,
              pages viewed, and interactions with the Estimator for analytics
              and operational purposes.
            </ListItemStyled>
            <Text>
              <strong>How We Use Information:</strong>
            </Text>
            <ListItemStyled>
              To provide, operate, and maintain the Estimator.
            </ListItemStyled>
            <ListItemStyled>
              To generate and deliver customized reports and information
              requested by users.
            </ListItemStyled>
            <ListItemStyled>
              To communicate with users, including responding to inquiries and
              providing customer support.
            </ListItemStyled>
            <ListItemStyled>
              To improve and enhance our services and user experience.
            </ListItemStyled>
            <ListItemStyled>
              To monitor usage patterns and conduct analytics.
            </ListItemStyled>
            <Text>
              <strong>Sharing of Information:</strong> We do not sell, rent, or
              lease your personal information. We may share your information:
            </Text>
            <ListItemStyled>
              With trusted third-party service providers assisting us in
              operating our website, conducting our business, or serving our
              users, provided these parties agree to keep this information
              confidential.
            </ListItemStyled>
            <ListItemStyled>
              When legally required or in response to valid legal processes or
              to comply with applicable laws.
            </ListItemStyled>
            <ListItemStyled>
              To protect our rights, property, or safety, or that of our users
              or others.
            </ListItemStyled>
            <Text>
              <strong>Data Security:</strong> RightSite implements reasonable
              and industry-standard security measures to protect your personal
              information from unauthorized access, disclosure, alteration, or
              destruction.
            </Text>
            <Text>
              <strong>Cookies and Tracking Technologies:</strong> We use cookies
              and similar tracking technologies to enhance user experience,
              analyze site usage, and provide personalized services.
            </Text>
            <Text>
              <strong>User Rights and Choices:</strong> Users may request access
              to, update, correct, or delete personal information provided to us
              by contacting us directly. Users may opt-out of receiving
              communications from us at any time.
            </Text>
            <Text>
              <strong>Third-Party Websites:</strong> Our Estimator may contain
              links to third-party websites. RightSite is not responsible for
              the privacy practices or content of these external sites.
            </Text>
            <Text>
              <strong>Children's Privacy:</strong> Our services are not directed
              at individuals under the age of 18, and we do not knowingly
              collect personal information from minors.
            </Text>
            <Text>
              <strong>Changes to Privacy Policy:</strong> RightSite may update
              this Privacy Policy periodically. Significant changes will be
              communicated through our website or via email.
            </Text>

            <Text>
              <strong>Contact Us:</strong> If you have questions or concerns
              regarding this Privacy Policy, please contact us at:
              <br />
              <br />
              RightSite LLC
              <br />
              info@rightsite.net
              <br />
              6218 Ash Street
              <br />
              Mission, KS 66205
              <br />
              United States
              <br />
              (816) 645-4924
            </Text>
          </PerfectScrollbar>
        </Content>
      </motion.div>
    </Container>
  );
};

export default TermsAndConditions;
